import type { Location } from '@/lib/types';

export interface UploadedMapImage {
  path: string;
  url: string;
}

export interface MapImageUploadUpdates {
  mainImagePath?: string;
  mainImageUrl?: string;
  mapImageStoragePath: string;
  mapImageUrl: string;
  originalMapStoragePath: string;
  originalMapUrl: string;
}

/** Build the location fields that must move together when its map is replaced. */
export function buildMapImageUploadUpdates(
  location: Pick<Location, 'images'>,
  image: UploadedMapImage
): MapImageUploadUpdates {
  // Replacing the background image must not modify any existing map state.
  const updates: MapImageUploadUpdates = {
    mapImageUrl: image.url,
    mapImageStoragePath: image.path,
    originalMapUrl: image.url,
    originalMapStoragePath: image.path,
  };

  // With no gallery image, the map is also the location card's cover image.
  if (!location.images?.length) {
    updates.mainImageUrl = image.url;
    updates.mainImagePath = image.path;
  }

  return updates;
}

/** Replaced map assets can share paths, so delete each old object only once. */
export function getReplacedMapStoragePaths(
  location: Pick<
    Location,
    | 'mapColorStoragePath'
    | 'mapImageStoragePath'
    | 'mapLandStoragePath'
    | 'mapSymbolsStoragePath'
    | 'originalMapStoragePath'
  >,
  replacementPath: string
): string[] {
  const retainedLayerPaths = new Set([
    location.mapColorStoragePath,
    location.mapLandStoragePath,
    location.mapSymbolsStoragePath,
  ].filter((path): path is string => Boolean(path)));

  return Array.from(new Set([
    location.mapImageStoragePath,
    location.originalMapStoragePath,
  ]
    .filter((path): path is string => Boolean(path))
    .filter((path) => path !== replacementPath && !retainedLayerPaths.has(path))));
}
